import type { TaskizeResult } from "~/api/intentTypes";
import { CONFIDENCE_PRECHECK, dropCommitted, existingTitles, hashKey } from "./dedupe";

/**
 * The tray's row model — what the extraction becomes once a person can edit it.
 *
 * `diff` is deliberately absent. kms declared `new | updated | resolved`, produced only `new`,
 * and rendered none of the three (§E6.6); a type that promises a distinction nothing makes is
 * worse than no type. `addNudge` is absent for the same reason — stored, toggled, never read
 * (§E6.5). The per-task *remind before due* select is the affordance that survived.
 */

export interface RowBase {
  key: string;
  checked: boolean;
  expanded: boolean;
  confidence: number;
  /** Static text. There is no anchor to jump to — the server sends none (§0 C6). */
  sourceQuote: string | null;
}

export interface TaskRowModel extends RowBase {
  kind: "task";
  title: string;
  details: string | null;
  /** The model's guess at a person, kept as the `<select>`'s "Unassigned (Ada?)" hint. */
  assigneeHint: string | null;
  assigneeId: string | null;
  /** `datetime-local` value, or "" for none. */
  due: string;
  dueHint: string | null;
  /** Minutes before `due`; null means no reminder. Only offered once a due date is set. */
  remindLeadMinutes: number | null;
}

export interface DecisionRowModel extends RowBase {
  kind: "decision";
  statement: string;
  status: string | null;
  decidedByHint: string | null;
  dateHint: string | null;
}

export interface ReminderRowModel extends RowBase {
  kind: "reminder";
  title: string;
  whenHint: string | null;
}

export type TaskizeRow = TaskRowModel | DecisionRowModel | ReminderRowModel;

export interface TaskizeRows {
  tasks: TaskRowModel[];
  decisions: DecisionRowModel[];
  reminders: ReminderRowModel[];
}

export const EMPTY_ROWS: TaskizeRows = { tasks: [], decisions: [], reminders: [] };

export function rowCount(rows: TaskizeRows): number {
  return rows.tasks.length + rows.decisions.length + rows.reminders.length;
}

export function checkedCount(rows: TaskizeRows): number {
  return [...rows.tasks, ...rows.decisions, ...rows.reminders].filter((row) => row.checked).length;
}

/**
 * Extraction → rows, with the document's existing records already removed.
 *
 * Tasks and decisions are deduped against what the markdown already records; **reminders never
 * are**, because two reminders about the same thing at different times are two different
 * intentions.
 */
export function buildRows(result: TaskizeResult, documentMarkdown: string): TaskizeRows {
  const existing = existingTitles(documentMarkdown);

  const tasks = dropCommitted(
    result.tasks.map((task) => ({ ...task, title: task.title.trim() })),
    existing,
  ).map(
    (task, index): TaskRowModel => ({
      kind: "task",
      key: hashKey("task", index, task.title),
      checked: task.confidence >= CONFIDENCE_PRECHECK,
      expanded: false,
      confidence: task.confidence,
      sourceQuote: task.sourceQuote,
      title: task.title,
      details: task.details,
      assigneeHint: task.assigneeHint,
      assigneeId: null,
      due: "",
      dueHint: task.dueHint,
      remindLeadMinutes: null,
    }),
  );

  const decisions = dropCommitted(
    result.decisions.map((decision) => ({
      ...decision,
      title: decision.statement.trim(),
    })),
    existing,
  ).map(
    (decision, index): DecisionRowModel => ({
      kind: "decision",
      key: hashKey("decision", index, decision.statement),
      checked: decision.confidence >= CONFIDENCE_PRECHECK,
      expanded: false,
      confidence: decision.confidence,
      sourceQuote: decision.sourceQuote,
      statement: decision.statement.trim(),
      status: decision.status,
      decidedByHint: decision.decidedByHint,
      dateHint: decision.dateHint,
    }),
  );

  const reminders = result.reminders.map(
    (reminder, index): ReminderRowModel => ({
      kind: "reminder",
      key: hashKey("reminder", index, reminder.title),
      checked: reminder.confidence >= CONFIDENCE_PRECHECK,
      expanded: false,
      confidence: reminder.confidence,
      sourceQuote: reminder.sourceQuote,
      title: reminder.title.trim(),
      whenHint: reminder.whenHint,
    }),
  );

  return { tasks, decisions, reminders };
}
